import { logger } from '@heidi/runtime/logger';
import { TrackingKey } from '@heidi/runtime/performance';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import type { UpdateStructureViaChatRequest } from '../../contracts';
import {
  GENERATE_STRUCTURE_VIA_CHAT_STREAMING_KEY,
  GET_TEMPLATE_CREATION_RECORD_BY_ID,
} from '../../contracts';
import {
  selectedVersionControlIdAtomFamily,
  templateCreationRecordIdAtom,
} from '../template-builder/atoms';
import {
  exampleGenerationStreamStateAtomFamily,
  structureGenerationStreamStateAtomFamily,
} from './atoms';
import { trackStreamRun } from './streamPerfTracking';
import {
  appendToVersionControlField,
  createStreamStateHandlers,
  updateStreamStates,
  updateVersionControlInQueryData,
} from './streamStateHelpers';
import { streamStructureViaChatGeneration } from './streaming';
import { TemplateStructureStreamError } from './templateStructureStreamError';
import { type AnimationType, StreamStatus } from './types';

export { GENERATE_STRUCTURE_VIA_CHAT_STREAMING_KEY };

export type GenerateStructureViaChatInput = UpdateStructureViaChatRequest & {
  target_version_control_id: string;
  animationType: AnimationType;
};

export const useMutateGenerateStructureViaChat = () => {
  const templateCreationRecordId = useAtomValue(templateCreationRecordIdAtom) ?? '';
  const queryClient = useQueryClient();
  const store = useStore();

  const setSelectedVersionControlId = useSetAtom(
    selectedVersionControlIdAtomFamily(templateCreationRecordId)
  );

  const queryKey = [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }];

  return useMutation({
    mutationKey: [GENERATE_STRUCTURE_VIA_CHAT_STREAMING_KEY],
    onMutate: ({ target_version_control_id, animationType }) => {
      // Capture the currently selected version control ID before switching to mock
      const previouslySelectedVersionControlId = store.get(
        selectedVersionControlIdAtomFamily(templateCreationRecordId)
      );

      const structureStreamStateAtom = structureGenerationStreamStateAtomFamily({
        templateCreationRecordId,
        versionControlId: target_version_control_id,
      });

      const exampleStreamStateAtom = exampleGenerationStreamStateAtomFamily({
        templateCreationRecordId,
        versionControlId: target_version_control_id,
      });

      updateStreamStates({
        store,
        atoms: [structureStreamStateAtom],
        status: StreamStatus.WAITING_START,
        animationType,
      });

      return {
        previouslySelectedVersionControlId,
        structureStreamStateAtom,
        exampleStreamStateAtom,
      };
    },
    mutationFn: async ({
      target_version_control_id,
      ...data
    }: GenerateStructureViaChatInput) => {
      // Reset state

      const structureStreamStateAtom = structureGenerationStreamStateAtomFamily({
        templateCreationRecordId,
        versionControlId: target_version_control_id,
      });
      const structureGenerationStreamState = store.get(structureStreamStateAtom);

      return trackStreamRun(
        TrackingKey.TEMPLATE_STRUCTURE_CHAT_EDIT,
        undefined,
        async (markFirstToken) => {
          let accumulatedStructure = '';

          let isFirstChunk = true;

          await streamStructureViaChatGeneration(
            {
              data,
              signal: structureGenerationStreamState.abortController.signal,
              templateCreationRecordId: templateCreationRecordId ?? '',
              targetVersionControlId: target_version_control_id,
            },
            {
              ...createStreamStateHandlers({
                store,
                streamStateAtom: structureStreamStateAtom,
                onMessageCallback: (chunk) => {
                  if (isFirstChunk) {
                    markFirstToken();
                    updateStreamStates({
                      store,
                      atoms: [structureStreamStateAtom],
                      status: StreamStatus.STREAMING,
                    });
                    updateVersionControlInQueryData({
                      queryClient,
                      queryKey,
                      versionControlId: target_version_control_id,
                      field: 'template_structure',
                      updater: () => '',
                    });
                    isFirstChunk = false;
                  }
                  accumulatedStructure += chunk.data;
                  appendToVersionControlField({
                    queryClient,
                    queryKey,
                    versionControlId: target_version_control_id,
                    field: 'template_structure',
                    chunk,
                  });
                },
              }),
              onStreamErrorFrame: (frame) => {
                throw new TemplateStructureStreamError(
                  frame.error,
                  target_version_control_id,
                  frame.message
                );
              },
            }
          );

          return accumulatedStructure;
        }
      );
    },

    onSuccess: (_, __, context) => {
      const { structureStreamStateAtom } = context ?? {};
      if (structureStreamStateAtom) {
        updateStreamStates({
          store,
          atoms: [structureStreamStateAtom],
          status: StreamStatus.IDLE,
        });
      }
    },

    onError: (error: Error, _, context) => {
      const {
        previouslySelectedVersionControlId,
        structureStreamStateAtom,
        exampleStreamStateAtom,
      } = context ?? {};

      if (structureStreamStateAtom && exampleStreamStateAtom) {
        updateStreamStates({
          store,
          atoms: [structureStreamStateAtom, exampleStreamStateAtom],
          status: StreamStatus.ERROR,
        });
      }

      // Revert to the previously selected version control ID
      setSelectedVersionControlId(previouslySelectedVersionControlId);
      logger.error(error);
    },
  });
};
